import { Badge, Body, Box, Button, Icon, Image } from '@hover/blueprint';
import type { IconProp } from '@hover/icons';
import React from 'react';

import { Link } from 'src/shared/components';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';
import type { ButtonPressed } from 'src/shared/telemetry';

interface InfoBannerProps {
  title?: string;
  body?: string;
  icon: IconProp;
  showLearnMoreLink?: boolean;
  learnMoreLink?: string;
  learnMoreText?: string;
  illustration: string;
  illustrationAlt: string;
  optionalTrackingProperties?: Partial<ButtonPressed & { job_id: number }>;
  isNewFeature?: boolean;
}

export const InfoBanner = ({
  title,
  body,
  icon,
  learnMoreLink,
  learnMoreText,
  illustration,
  illustrationAlt,
  optionalTrackingProperties,
  isNewFeature = false,
  showLearnMoreLink = true,
}: InfoBannerProps) => {
  const tCommon = useTranslate(TranslationNamespace.COMMON);
  return (
    <Box
      alignItems="center"
      backgroundColor="neutral.100"
      borderRadius={500}
      flexDirection={{ base: 'column', tablet: 'row' }}
      height={{ base: '350px', tablet: 'auto' }}
      justifyContent="space-between"
      minH="350px"
      overflow="hidden"
      paddingX={400}
      pt={{ base: 500, tablet: 0 }}
      px={800}
    >
      <Box flexDirection="column" justifyContent="center">
        <Icon color="neutral.700" icon={icon} marginLeft={0} size="huge" />

        {title && (
          <Body
            as="span"
            color="neutral.700"
            fontSize="34px"
            lineHeight={700}
            mb={100}
          >
            {title}
            {isNewFeature && (
              <Badge colorScheme="warning" marginLeft={300}>
                {tCommon('beta')}
              </Badge>
            )}
          </Body>
        )}
        {body && <Body mb={0}>{body}</Body>}
        {showLearnMoreLink && learnMoreLink && learnMoreText && (
          <Link
            aria-label={learnMoreText}
            isExternal
            mb={100}
            mt={600}
            optionalTrackingProperties={optionalTrackingProperties}
            target="_blank"
            to={learnMoreLink}
          >
            <Button as="span" size="large">
              {learnMoreText}
            </Button>
          </Link>
        )}
      </Box>
      <Image
        alt={illustrationAlt}
        borderRadius={25}
        src={illustration}
        width="350px"
      />
    </Box>
  );
};
